import { createContext, useEffect, useState } from 'react';
import type { FC, ReactNode } from 'react';
import _ from 'lodash';
import { THEMES } from '../config';

interface Settings {
  compact?: boolean;
  direction?: 'ltr' | 'rtl';
  responsiveFontSizes?: boolean;
  roundedCorners?: boolean;
  theme?: string;
  wrapperStyle: {
    paddingTop?: number;
    paddingBottom?: number;
    [key: string]: any;
  };
}

export interface SettingsContextValue {
  settings: Settings;
  saveSettings: (update: Settings) => void;
  scrollAreaSettings: (update: Settings) => void;
  resetScrollPostion: () => void;
}

interface SettingsProviderProps {
  children?: ReactNode;
}

const initialSettings: Settings = {
  compact: true,
  direction: 'ltr',
  responsiveFontSizes: true,
  roundedCorners: true,
  theme: THEMES.LIGHT,
  wrapperStyle: {
    paddingTop: 56,
    paddingBottom: 0,
    // paddingTop: 110, // PageAppBar(56) + Tab(54) 높이
    // paddingBottom: 56 // BottomNavi 높이
  },
};

export const restoreSettings = (): Settings | null => {
  let settings = null;

  try {
    const storedData: string | null = window.localStorage.getItem('settings');

    if (storedData) {
      settings = JSON.parse(storedData);
    } else {
      settings = {
        compact: true,
        direction: 'ltr',
        responsiveFontSizes: true,
        roundedCorners: true,
        theme: window.matchMedia('(prefers-color-scheme: dark)').matches ? THEMES.ONE_DARK : THEMES.LIGHT,
        wrapperStyle: initialSettings.wrapperStyle,
      };
    }
  } catch (err) {
    console.error(err);
    // If stored data is not a strigified JSON this will fail,
    // that's why we catch the error
  }

  return settings;
};

export const storeSettings = (settings: Settings): void => {
  window.localStorage.setItem('settings', JSON.stringify(settings));
};

const SettingsContext = createContext<SettingsContextValue>({
  settings: initialSettings,
  saveSettings: () => {},
  scrollAreaSettings: () => {},
  resetScrollPostion: () => {},
});

export const SettingsProvider: FC<SettingsProviderProps> = (props) => {
  const { children } = props;
  const [settings, setSettings] = useState<Settings>(initialSettings);

  const handleScrollAreaSettings = (update: Settings) => {
    const { wrapperStyle } = settings;
    const mergedWrapperStyle = _.merge({}, wrapperStyle, update.wrapperStyle);
    setSettings({
      ...settings,
      wrapperStyle: mergedWrapperStyle,
    });
  };

  // 스크롤을 최상단으로 이동처리
  const hadleResetScrollPostion = () => {
    const contentEl = document.getElementById('mainlayout-content');
    if (contentEl) {
      contentEl.scrollTo(0, 0);
    }
  };

  useEffect(() => {
    const restoredSettings = restoreSettings();

    if (restoredSettings) {
      setSettings(restoredSettings);
    }
  }, []);

  const saveSettings = (updatedSettings: Settings): void => {
    setSettings(updatedSettings);
    storeSettings(updatedSettings);
  };

  return (
    <SettingsContext.Provider
      value={{
        settings,
        saveSettings,
        scrollAreaSettings: handleScrollAreaSettings,
        resetScrollPostion: hadleResetScrollPostion,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const SettingsConsumer = SettingsContext.Consumer;

export default SettingsContext;
